import { useCallback, useEffect, useState } from 'react';
import { MarkMonthlyPayslipsAsPaid, MarkPaidCheck } from '../../../../api/Payroll/monthlyPayroll';
import { MonthlyPayrollInput, WpsConfirmWarning } from '../../../../models/payroll';
import { findApiError } from '../../../../utils/requestHandling';

interface UseWpsMarkPaidOptions {
  /** UAE only. Everywhere else Mark Paid goes straight to the server, as it always has. */
  checkWps: boolean;
  year: number;
  month: number;
  selectedIds: Set<number>;
  onMarked: () => Promise<void> | void;
  onFailed: (error: unknown) => void;
}

/**
 * Mark Paid on the Payroll Table. On a UAE company the server refuses the write until the client has shown its warnings
 * (design 8.4), so this asks first, shows what comes back, and resends with ConfirmWpsWarnings when HR goes ahead.
 */
export const useWpsMarkPaid = ({ checkWps, year, month, selectedIds, onMarked, onFailed }: UseWpsMarkPaidOptions) => {
  const [warnings, setWarnings] = useState<WpsConfirmWarning[] | null>(null);
  const [busy, setBusy] = useState(false);

  // The warning names the rows it was asked about, so it does not outlive that selection.
  useEffect(() => { setWarnings(null); }, [selectedIds, year, month]);

  const input = useCallback(
    () => ({ year, month, payslipIds: Array.from(selectedIds) } as MonthlyPayrollInput),
    [year, month, selectedIds],
  );

  const showCheck = useCallback(async (): Promise<boolean> => {
    const check = await MarkPaidCheck(input());
    const found = check.payload?.warnings ?? [];
    if (found.length === 0) return false;
    setWarnings(found);
    return true;
  }, [input]);

  const send = useCallback(async (confirmWpsWarnings: boolean) => {
    try {
      await MarkMonthlyPayslipsAsPaid({ ...input(), ...(confirmWpsWarnings ? { confirmWpsWarnings } : {}) });
      setWarnings(null);
      await onMarked();
    } catch (e) {
      // A hold or the setting moved after the check. The fresh warning names the people, which the refusal cannot.
      if (checkWps && findApiError(e, 'wpsMarkPaidNeedsConfirm', 'wpsMarkPaidHeldRefused')) {
        try {
          if (await showCheck()) return;
        } catch { /* report the original refusal below */ }
      }
      onFailed(e);
    }
  }, [checkWps, input, onFailed, onMarked, showCheck]);

  const request = useCallback(async () => {
    if (selectedIds.size === 0) return;
    setBusy(true);
    try {
      if (checkWps && await showCheck()) return;
      await send(false);
    } catch (e) {
      onFailed(e);
    } finally {
      setBusy(false);
    }
  }, [checkWps, onFailed, selectedIds, send, showCheck]);

  const confirm = useCallback(async () => {
    setBusy(true);
    try {
      await send(true);
    } finally {
      setBusy(false);
    }
  }, [send]);

  const dismiss = useCallback(() => setWarnings(null), []);

  return { warnings, busy, request, confirm, dismiss };
};
